import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination } from '../components/TableControls';
import { formatMonthYear } from '../utils/dateUtils';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';

export default function Reconciliations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [msg, setMsg] = useState('');
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(reconciliations, 'month_year', 'desc');

  const load = () => api.get('/reconciliation').then(r => setReconciliations(r.data));
  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
    load();
  }, []);

  useAutoSelectSingle(stores, storeId, setStoreId);

  const generate = async () => {
    setMsg('');
    if (!storeId) {
      setMsg('Por favor selecciona una tienda antes de generar.');
      return;
    }
    try {
      const monthYear = `${year}-${String(month).padStart(2, '0')}`;
    const res = await api.post(`/reconciliation/generate?store_id=${storeId}&month_year=${monthYear}`);
      setMsg('¡Conciliación generada!');
      load();
      navigate(`/reconciliations/${res.data.id}`);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`¿Eliminar conciliación de ${r.store_name} - ${r.month_year}?${r.status === 'finalized' ? ' Esto también desbloqueará el mes.' : ''}`)) return;
    setMsg('');
    try {
      const res = await api.delete(`/reconciliation/${r.id}`);
      setMsg(res.data.message);
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="page-header"><h1>Conciliaciones</h1></div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Generar Conciliación</h3>
        {msg && <div className={`alert ${msg.includes('Error') || msg.includes('No completed') || msg.includes('finalized') || msg.includes('Por favor') ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}
        <div className="form-row">
          <div className="form-group" style={{ minWidth: 180 }}>
            <label>Tienda</label>
            <select value={storeId} onChange={e => setStoreId(e.target.value)}>
              <option value="">Seleccionar tienda</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 100 }}>
            <label>Año</label>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} required min="2000" max="2100" />
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>Mes</label>
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))} required>
              {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={generate}>Generar</button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Todas las Conciliaciones</h3>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Tienda</SortableTh>
                <SortableTh colKey="month_year" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Mes</SortableTh>
                <SortableTh colKey="status" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Estado</SortableTh>
                <SortableTh colKey="total_expected" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Esperado</SortableTh>
                <SortableTh colKey="total_counted" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Contado</SortableTh>
                <SortableTh colKey="total_discrepancy" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Discrepancia</SortableTh>
                <SortableTh colKey="waste_percentage" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>% Merma</SortableTh>
                <SortableTh colKey="discrepancy_percentage" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>% Disc.</SortableTh>
                <th>Alertas</th>
                <SortableTh colKey="finalized_by_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Por</SortableTh>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {reconciliations.length === 0 && <tr><td colSpan={11}>Sin conciliaciones aún</td></tr>}
              {paginated.map(r => (
                <tr key={r.id}>
                  <td>{r.store_name}</td>
                  <td>{formatMonthYear(r.month_year)}</td>
                  <td>
                    <span className={`badge ${r.status === 'finalized' ? 'badge-success' : r.status === 'invalid' ? 'badge-danger' : 'badge-warning'}`}>
                      {r.status === 'finalized' ? 'Finalizado' : r.status === 'invalid' ? 'Inválido' : 'Borrador'}
                    </span>
                  </td>
                  <td>{r.total_expected.toFixed(1)}</td>
                  <td>{r.total_counted.toFixed(1)}</td>
                  <td>{r.total_discrepancy.toFixed(1)}</td>
                  <td>{r.waste_percentage}%</td>
                  <td>{r.discrepancy_percentage}%</td>
                  <td>
                    {r.waste_threshold_exceeded && <span className="badge badge-danger" style={{ marginRight: 4 }}>MERMA</span>}
                    {r.discrepancy_threshold_exceeded && <span className="badge badge-danger">DISC.</span>}
                  </td>
                  <td>{r.finalized_by_name || '—'}</td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => navigate(`/reconciliations/${r.id}`)}>
                      Ver
                    </button>
                    {(user?.role === 'admin' || r.status !== 'finalized') && (
                      <>
                        {' '}
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>Eliminar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>
    </div>
  );
}
